import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, type Auth } from "firebase/auth";
import logger from "./logger";

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
}

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let googleProvider: GoogleAuthProvider | null = null;

/**
 * Build Firebase config from environment variables
 * @returns FirebaseConfig object or null if required fields are missing
 */
const buildFirebaseConfig = (): FirebaseConfig | null => {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

  // Required fields
  if (!apiKey || !authDomain || !projectId) {
    return null;
  }

  const config: FirebaseConfig = {
    apiKey,
    authDomain,
    projectId,
  };

  // Optional fields
  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;

  if (storageBucket) {
    config.storageBucket = storageBucket;
  }
  if (messagingSenderId) {
    config.messagingSenderId = messagingSenderId;
  }
  if (appId) {
    config.appId = appId;
  }

  return config;
};

/**
 * Initialize Firebase app if config is available
 * @returns Firebase app instance or null if config is missing
 */
export const initializeFirebase = (): FirebaseApp | null => {
  if (firebaseApp) {
    return firebaseApp;
  }

  const config = buildFirebaseConfig();
  if (!config) {
    return null;
  }

  try {
    // Check if Firebase is already initialized
    const existingApp = getApps().find((app) => app.name === "[DEFAULT]");
    if (existingApp) {
      firebaseApp = existingApp;
    } else {
      firebaseApp = initializeApp(config);
    }

    return firebaseApp;
  } catch (error) {
    logger._console.error("Failed to initialize Firebase:", error);
    return null;
  }
};

/**
 * Get Firebase Auth instance
 * @returns Firebase Auth instance or null if not initialized
 */
export const getFirebaseAuth = (): Auth | null => {
  if (firebaseAuth) {
    return firebaseAuth;
  }

  const app = initializeFirebase();
  if (!app) {
    return null;
  }

  firebaseAuth = getAuth(app);
  return firebaseAuth;
};

/**
 * Get Google Auth Provider instance
 * @returns GoogleAuthProvider instance or null if Firebase is not initialized
 */
export const getFirebaseGoogleAuthProvider = (): GoogleAuthProvider | null => {
  if (googleProvider) {
    return googleProvider;
  }

  const auth = getFirebaseAuth();
  if (!auth) {
    return null;
  }

  googleProvider = new GoogleAuthProvider();
  return googleProvider;
};

/**
 * Check if Firebase is available (config exists and can be initialized)
 * @returns true if Firebase can be used
 */
export const isFirebaseAvailable = (): boolean => {
  const config = buildFirebaseConfig();
  return config !== null;
};
